import { SplashScreen } from "@capacitor/splash-screen";
import { isNativeApp } from "./platform";

// Splash dismissal for the native shell.
//
// capacitor.config.ts sets launchAutoHide:false, so the splash stays up until
// something calls hide(). That is deliberate: the shell loads the SPA from the
// REMOTE origin, and device logs showed WebContent/GPU processes taking 4-13s
// to launch on a cold start. The old timer-based auto-hide (1200ms) dismissed
// the splash long before the SPA painted, leaving a blank white/black screen
// that read as a hung app.
//
// The contract that comes with launchAutoHide:false: every entry point the
// webview can land on MUST hide the splash, or the app is stuck on it forever.
// There are exactly two — this one (the SPA) and mobile/www/offline.html (the
// server.errorPath cold-launch fallback), which carries its own inline hide.
// If a third is ever added, it needs one too.

const BACKSTOP_MS = 8000;

/**
 * Hide the splash once the first frame has painted. No-ops on web.
 *
 * Fires on a double rAF (one frame scheduled, one frame rendered) so the splash
 * covers the pre-paint gap rather than being swapped for an empty root div. A
 * backstop timer guarantees dismissal even if paint never reports.
 */
export function hideSplashWhenReady(): void {
  if (!isNativeApp()) return;

  let done = false;
  const hide = () => {
    if (done) return;
    done = true;
    // Never let a splash failure take down app startup.
    SplashScreen.hide().catch(() => {});
  };

  requestAnimationFrame(() => requestAnimationFrame(hide));
  setTimeout(hide, BACKSTOP_MS);
}
